/**
 * LES deux téléchargements qui précèdent un éditeur de rédaction : le module de
 * l'éditeur (SuperDoc compris) et le `.docx` vierge d'une page neuve.
 *
 * L'admission d'un travail de rédaction monte l'éditeur hors écran sous une
 * borne qui court PENDANT ces téléchargements. Le module se charge donc dès
 * qu'un travail s'annonce (`redaction_plan_request`), et l'état des deux est
 * rendu dans le message d'un montage qui n'arrive pas : c'est ce qui départage
 * un poste lent à télécharger d'un éditeur chargé qui ne se construit pas
 * (`hasInstance:false`, prod 2026-10-09 job 9289332e, puis 19-21/10).
 */

import { chargerLesPolicesDeSubstitution } from './policesDeSubstitution';

type EtatDeChargement =
  | { readonly statut: 'jamais' }
  | { readonly statut: 'en-cours'; readonly debut: number }
  | { readonly statut: 'charge'; readonly debut: number; readonly fin: number }
  | { readonly statut: 'echec'; readonly cause: string };

/** Le `.docx` vierge servi en asset statique par Vite. */
const BLANK_TEMPLATE_URL = '/templates/blank-with-page-numbers.docx';

let chargementDuModule: Promise<typeof import('./RedactionCanvas')> | null = null;
let etatDuModule: EtatDeChargement = { statut: 'jamais' };
let etatDuModeleVierge: EtatDeChargement = { statut: 'jamais' };

/** Suit une promesse de téléchargement dans `ecrire`, sans changer son issue. */
function suivre<T>(promesse: Promise<T>, ecrire: (etat: EtatDeChargement) => void): Promise<T> {
  const debut = performance.now();
  ecrire({ statut: 'en-cours', debut });
  return promesse.then(
    (valeur) => {
      ecrire({ statut: 'charge', debut, fin: performance.now() });
      return valeur;
    },
    (erreur: unknown) => {
      ecrire({ statut: 'echec', cause: erreur instanceof Error ? erreur.message : String(erreur) });
      throw erreur;
    },
  );
}

/**
 * Le module de l'éditeur, téléchargé une fois ; les appels suivants rendent la
 * même promesse. Un échec ne condamne pas les appels suivants : le prochain
 * retente (réseau revenu, nouveau déploiement).
 */
export function chargerLEditeurDeRedaction(): Promise<typeof import('./RedactionCanvas')> {
  if (!chargementDuModule) {
    // Les polices de substitution se chargent en parallèle du module : l'éditeur
    // mesure le texte au montage, elles doivent être là avant (policesDeSubstitution.ts).
    chargementDuModule = Promise.all([
      suivre(import('./RedactionCanvas'), (etat) => { etatDuModule = etat; }),
      chargerLesPolicesDeSubstitution(),
    ]).then(([module]) => module).catch((erreur: unknown) => {
      chargementDuModule = null;
      throw erreur;
    });
  }
  return chargementDuModule;
}

/** Le `.docx` vierge d'un montage, téléchargé à chaque montage comme avant. */
export function chargerLeModeleVierge(): Promise<Blob> {
  return suivre(
    fetch(BLANK_TEMPLATE_URL).then((reponse) => {
      if (!reponse.ok) throw new Error(`template fetch failed: ${reponse.status}`);
      return reponse.blob();
    }),
    (etat) => { etatDuModeleVierge = etat; },
  );
}

function decrire(nom: string, etat: EtatDeChargement, maintenant: number): string {
  switch (etat.statut) {
    case 'jamais':
      return `${nom}=never-requested`;
    case 'en-cours':
      return `${nom}=loading for ${Math.round(maintenant - etat.debut)}ms`;
    case 'charge':
      return `${nom}=loaded in ${Math.round(etat.fin - etat.debut)}ms, ${Math.round(maintenant - etat.fin)}ms ago`;
    case 'echec':
      return `${nom}=failed (${etat.cause})`;
  }
}

/**
 * L'état des deux téléchargements, en une ligne, pour le message d'un montage
 * qui n'arrive pas. Le modèle vierge ne concerne qu'une page neuve : un montage
 * de brouillon ou de pièce ne le demande pas.
 */
export function decrireLeChargementDeLEditeur(): string {
  const maintenant = performance.now();
  return `${decrire('editorModule', etatDuModule, maintenant)}; ${decrire('blankTemplate', etatDuModeleVierge, maintenant)}`;
}
